import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { ANCHOR_MOMENTS, ANCHOR_MOMENT_MAX } from '@goaltree/backend/convex/lib/habitStacks'
import { PopoverClose } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import type { TFunction } from 'i18next'
import { Link2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { HabitIcon } from '@/components/habit-icon'
import { doneTodayAt } from '@/components/task-meta'
import { keyToDate, todayKey } from '@/lib/day'
import { patchHabit } from '@/lib/goals-optimistic'
import { completeNode } from '@/lib/nodes-optimistic'
import { dueOnDate } from '@/lib/schedule'
import { taskTitle } from '@/lib/tags'

type Habits = NonNullable<ReturnType<typeof useHabits>>
type Habit = Habits[number]
type Anchored = { anchorHabitId?: Id<'nodes'> | null; anchorMoment?: string | null }

function useHabits() {
  const { isAuthenticated } = useConvexAuth()
  return useQuery(api.nodes.getHabits, isAuthenticated ? {} : 'skip')
}

export function useLiveHabit(id: Id<'nodes'> | undefined): Habit | undefined {
  const habits = useHabits()
  return id ? habits?.find((h) => h._id === id) : undefined
}

function anchorText(t: TFunction, a: Anchored, habits: Habits | undefined): string | null {
  if (a.anchorHabitId) {
    const h = habits?.find((x) => x._id === a.anchorHabitId)
    return h ? t('inbox.anchorAfterHabit', { title: taskTitle(h) }) : null
  }
  if (!a.anchorMoment) return null
  const moment = (ANCHOR_MOMENTS as readonly string[]).includes(a.anchorMoment)
    ? t(`inbox.anchorMoment.${a.anchorMoment}`)
    : a.anchorMoment
  return t('inbox.anchorAfterMoment', { moment })
}

// "After I pour my coffee" under a stacked habit's title.
export function HabitAnchorLine({ habit, className }: { habit: Anchored; className?: string }) {
  const { t } = useTranslation()
  const habits = useHabits()
  const text = anchorText(t, habit, habits)
  if (!text) return null
  return (
    <p className={cn('flex min-w-0 items-center gap-1 text-xs text-muted-foreground', className)}>
      <Link2 className="size-3 shrink-0" strokeWidth={2.2} />
      <span className="truncate">{text}</span>
    </p>
  )
}

export function HabitAnchorLineById({ id, className }: { id: Id<'nodes'>; className?: string }) {
  const habit = useLiveHabit(id)
  return habit ? <HabitAnchorLine habit={habit} className={className} /> : null
}

export function useAnchorText(habit: Anchored): string | null {
  const { t } = useTranslation()
  return anchorText(t, habit, useHabits())
}

// Would stacking `self` after `candidate` loop back to `self`?
function makesLoop(self: Id<'nodes'>, candidate: Habit, habits: Habits): boolean {
  let cur: Id<'nodes'> | null | undefined = candidate._id
  for (let i = 0; cur && i < 20; i++) {
    if (cur === self) return true
    cur = habits.find((h) => h._id === cur)?.anchorHabitId
  }
  return false
}

export function HabitAnchorPicker({
  taskId,
  value,
  onChange,
}: {
  taskId: Id<'nodes'>
  value: Anchored
  onChange: (next: Anchored) => void
}) {
  const { t } = useTranslation()
  const habits = useHabits()
  const setAnchor = useMutation(api.nodes.setHabitAnchor).withOptimisticUpdate(
    (store, { taskId, anchorHabitId, anchorMoment }) =>
      patchHabit(store, taskId, {
        anchorHabitId: anchorHabitId ?? null,
        anchorMoment: anchorHabitId
          ? null
          : anchorMoment?.trim().slice(0, ANCHOR_MOMENT_MAX) || null,
      }),
  )
  const [custom, setCustom] = useState('')
  const isPreset = (ANCHOR_MOMENTS as readonly string[]).includes(value.anchorMoment ?? '')

  function save(next: Anchored) {
    const prev = value
    onChange(next)
    setAnchor({
      taskId,
      ...(next.anchorHabitId ? { anchorHabitId: next.anchorHabitId } : {}),
      ...(next.anchorMoment ? { anchorMoment: next.anchorMoment } : {}),
    }).catch(() => onChange(prev))
  }

  const candidates = (habits ?? []).filter(
    (h) => h._id !== taskId && !makesLoop(taskId, h, habits!),
  )
  const row =
    'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted'
  const label = 'px-2 pt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60'

  return (
    <div className="flex max-h-80 flex-col gap-1 overflow-y-auto p-1">
      <p className="px-2 pt-1 pb-1 text-xs text-muted-foreground">{t('inbox.anchorHint')}</p>
      <p className={label}>{t('inbox.anchorMoments')}</p>
      {ANCHOR_MOMENTS.map((m) => (
        <PopoverClose
          key={m}
          onClick={() => save({ anchorMoment: m })}
          className={cn(row, value.anchorMoment === m && 'bg-muted font-semibold')}
        >
          {t('inbox.anchorAfterMoment', { moment: t(`inbox.anchorMoment.${m}`) })}
        </PopoverClose>
      ))}
      <form
        className="px-1 py-1"
        onSubmit={(e) => {
          e.preventDefault()
          if (custom.trim()) save({ anchorMoment: custom.trim() })
        }}
      >
        <input
          value={custom || (!isPreset ? (value.anchorMoment ?? '') : '')}
          onChange={(e) => setCustom(e.target.value)}
          maxLength={60}
          placeholder={t('inbox.anchorCustom')}
          className="w-full rounded-md border bg-background px-2 py-1.5 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        />
      </form>
      {candidates.length > 0 && <p className={label}>{t('inbox.anchorHabits')}</p>}
      {candidates.map((h) => (
        <PopoverClose
          key={h._id}
          onClick={() => save({ anchorHabitId: h._id })}
          className={cn(row, value.anchorHabitId === h._id && 'bg-muted font-semibold')}
        >
          <HabitIcon habit={h} className="size-5" />
          <span className="truncate">{taskTitle(h)}</span>
        </PopoverClose>
      ))}
      {(value.anchorHabitId || value.anchorMoment) && (
        <PopoverClose onClick={() => save({})} className={cn(row, 'text-muted-foreground')}>
          {t('inbox.anchorNone')}
        </PopoverClose>
      )}
    </div>
  )
}

// Weekly/monthly habits count as scheduled any day (no single due day here).
const scheduledToday = (period: string | null | undefined, days?: readonly number[] | null) =>
  period === 'weekly' ||
  period === 'monthly' ||
  dueOnDate(period, null, keyToDate(todayKey()), days)

// When a habit gets done (from any screen), surface the habits stacked after it.
export function HabitChainWatcher() {
  const { t } = useTranslation()
  const habits = useHabits()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) =>
    completeNode(store, taskId),
  )
  const doneBefore = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (!habits) return
    const done = new Set(habits.filter((h) => doneTodayAt(h.completions)).map((h) => h._id))
    const prev = doneBefore.current
    doneBefore.current = done
    if (!prev || document.visibilityState !== 'visible') return
    for (const id of done) {
      if (prev.has(id)) continue
      const next = habits.find(
        (h) =>
          h.anchorHabitId === id &&
          !done.has(h._id) &&
          // Its "Done" would tick a fast off instead of timing it.
          !h.measure?.fast &&
          scheduledToday(h.recursPeriod, h.recursDays),
      )
      if (!next) continue
      toast(t('inbox.chainNext', { title: taskTitle(next) }), {
        icon: <HabitIcon habit={next} className="size-5" />,
        duration: 8000,
        action: {
          label: t('inbox.chainDone'),
          onClick: () => void complete({ taskId: next._id }).catch(() => {}),
        },
      })
    }
  }, [habits, complete, t])

  return null
}
